import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiError } from '../lib/api';
import { Alert, EmptyState, LoadingBlock } from '../components/ui';
import { formatClock, formatDateTime } from '../lib/format';
import type { StartLists } from '../lib/types';

export function StartListsPage() {
  const [data, setData] = useState<StartLists | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notPublished, setNotPublished] = useState(false);
  const [query, setQuery] = useState('');

  useEffect(() => {
    const controller = new AbortController();
    api
      .startLists(controller.signal)
      .then((loaded) => {
        setData(loaded);
        setError(null);
      })
      .catch((err: unknown) => {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        if (err instanceof ApiError && err.status === 404) {
          setNotPublished(true);
          return;
        }
        setError(err instanceof ApiError ? err.message : 'Could not load the start lists.');
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, []);

  const needle = query.trim().toLowerCase();

  /** Reference keys of the swimmers matching the search, for highlighting. */
  const matches = useMemo(() => {
    if (!data || needle.length < 2) return new Set<string>();
    const found = new Set<string>();
    for (const heat of data.heats) {
      for (const lane of heat.lanes) {
        const haystack = `${lane.firstName} ${lane.lastName} ${lane.reference} ${lane.club}`.toLowerCase();
        if (haystack.includes(needle)) found.add(lane.reference);
      }
    }
    return found;
  }, [data, needle]);

  const unit = data?.seedingMode === 'lanes' ? 'Heat' : 'Wave';

  if (loading) return <LoadingBlock message="Loading start lists..." />;

  if (notPublished) {
    return (
      <div className="stack">
        <h1>Start lists</h1>
        <EmptyState title="Not published yet">
          <p>
            Start lists are generated once entries close and payments are confirmed. Check back
            closer to the event.
          </p>
          <p style={{ marginBottom: 0 }}>
            <Link to="/my-entry">Check your entry</Link> in the meantime.
          </p>
        </EmptyState>
      </div>
    );
  }

  if (error || !data) {
    return (
      <Alert variant="error" title="Start lists unavailable">
        <p>{error ?? 'Please try again shortly.'}</p>
      </Alert>
    );
  }

  const swimmerCount = data.heats.reduce((sum, heat) => sum + heat.lanes.length, 0);

  return (
    <div className="stack">
      <div className="spread">
        <div>
          <h1 style={{ marginBottom: 'var(--space-1)' }}>Start lists</h1>
          <p className="muted" style={{ margin: 0 }}>
            {data.heats.length} {unit.toLowerCase()}
            {data.heats.length === 1 ? '' : 's'} &middot; {swimmerCount} swimmers &middot; published{' '}
            {formatDateTime(data.publishedAt)}
          </p>
        </div>
        <button type="button" className="btn btn-secondary no-print" onClick={() => window.print()}>
          Print
        </button>
      </div>

      <div className="card card-tight no-print">
        <div className="field">
          <label htmlFor="startlist-search">Find a swimmer</label>
          <input
            id="startlist-search"
            type="search"
            placeholder="Name, club or reference"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-describedby="startlist-search-status"
          />
          <span className="field-hint" id="startlist-search-status" aria-live="polite">
            {needle.length < 2
              ? 'Type at least two characters to highlight matches.'
              : `${matches.size} match${matches.size === 1 ? '' : 'es'} highlighted below.`}
          </span>
        </div>
      </div>

      {data.heats.map((heat) => (
        <section className="heat" key={heat.heatNumber} aria-labelledby={`heat-${heat.heatNumber}`}>
          <div className="heat-header">
            <h2 className="heat-title" id={`heat-${heat.heatNumber}`}>
              {heat.name}
            </h2>
            <span className="heat-meta">
              {heat.lanes.length} swimmers
              {heat.scheduledAt && <> &middot; start {formatClock(heat.scheduledAt)}</>}
            </span>
          </div>

          <table>
            <caption className="visually-hidden">
              {heat.name}, {heat.lanes.length} swimmers
            </caption>
            <thead>
              <tr>
                <th scope="col">{data.seedingMode === 'lanes' ? 'Lane' : 'Pos'}</th>
                <th scope="col">Name</th>
                <th scope="col">Club</th>
                <th scope="col">Age band</th>
                <th scope="col">Predicted</th>
              </tr>
            </thead>
            <tbody>
              {heat.lanes.map((lane) => (
                <tr
                  key={lane.reference}
                  className={matches.has(lane.reference) ? 'highlight-row' : undefined}
                >
                  <td>
                    <span className="lane-number">{lane.lane}</span>
                  </td>
                  <td>
                    {lane.firstName} {lane.lastName}
                  </td>
                  <td className="muted">{lane.club || '-'}</td>
                  <td className="muted nowrap">{lane.ageGroup}</td>
                  <td className="mono nowrap">{lane.seedTimeFormatted}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ))}

      <p className="faint no-print">
        Entry references are not shown here. Use <Link to="/my-entry">Find my entry</Link> to confirm
        your own start details.
      </p>
    </div>
  );
}
